import { translateApiError } from '../../../../common/i18n.js'
import { errorMessageFromBody } from '../../../utils/api-error-message.js'
import { getCurrentLanguage } from '../../../utils/language.js'

/**
 * Typed Portal API client.
 *
 * Replaces raw fetch() calls + unsafe `as` casts across all PH views
 * with a single, type-safe helper layer.
 */

interface ApiSuccess<T> {
    data: T
}

interface ApiPaginatedSuccess<T> {
    data: T
    pagination: { total: number }
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string }
export type PaginatedResult<T> = { ok: true; data: T; total: number } | { ok: false; error: string }

/** The user-facing network-error string, translated into the active language. */
const networkError = (): string => translateApiError('NETWORK_ERROR', {}, getCurrentLanguage())

const parseError = async (res: Response): Promise<string> => {
    const body: unknown = await res.json().catch(() => null)
    return errorMessageFromBody(body, res.status, getCurrentLanguage())
}

export const portalGet = async <T>(url: string): Promise<ApiResult<T>> => {
    try {
        const res = await fetch(url)
        if (!res.ok) return { ok: false, error: await parseError(res) }
        const body = (await res.json()) as ApiSuccess<T>
        return { ok: true, data: body.data }
    } catch {
        return { ok: false, error: networkError() }
    }
}

export const portalGetPaginated = async <T>(url: string): Promise<PaginatedResult<T>> => {
    try {
        const res = await fetch(url)
        if (!res.ok) return { ok: false, error: await parseError(res) }
        const body = (await res.json()) as ApiPaginatedSuccess<T>
        return { ok: true, data: body.data, total: body.pagination?.total ?? 0 }
    } catch {
        return { ok: false, error: networkError() }
    }
}

export const portalPost = async <T>(url: string, data?: unknown): Promise<ApiResult<T>> => {
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: data !== undefined ? JSON.stringify(data) : undefined,
        })
        if (!res.ok) return { ok: false, error: await parseError(res) }
        if (res.status === 204 || res.headers.get('content-length') === '0') {
            return { ok: true, data: undefined as T }
        }
        const body = (await res.json()) as ApiSuccess<T>
        return { ok: true, data: body.data }
    } catch {
        return { ok: false, error: networkError() }
    }
}

export const portalPut = async <T>(url: string, data: unknown): Promise<ApiResult<T>> => {
    try {
        const res = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        })
        if (!res.ok) return { ok: false, error: await parseError(res) }
        if (res.status === 204 || res.headers.get('content-length') === '0') {
            return { ok: true, data: undefined as T }
        }
        const body = (await res.json()) as ApiSuccess<T>
        return { ok: true, data: body.data }
    } catch {
        return { ok: false, error: networkError() }
    }
}

export const portalPatch = async <T>(url: string, data?: unknown): Promise<ApiResult<T>> => {
    try {
        const res = await fetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: data !== undefined ? JSON.stringify(data) : undefined,
        })
        if (!res.ok) return { ok: false, error: await parseError(res) }
        if (res.status === 204 || res.headers.get('content-length') === '0') {
            return { ok: true, data: undefined as T }
        }
        const body = (await res.json()) as ApiSuccess<T>
        return { ok: true, data: body.data }
    } catch {
        return { ok: false, error: networkError() }
    }
}

export const portalDelete = async <T = void>(url: string): Promise<ApiResult<T>> => {
    try {
        const res = await fetch(url, { method: 'DELETE' })
        if (!res.ok) return { ok: false, error: await parseError(res) }
        if (res.status === 204 || res.headers.get('content-length') === '0') {
            return { ok: true, data: undefined as T }
        }
        const body = (await res.json()) as ApiSuccess<T>
        return { ok: true, data: body.data }
    } catch {
        return { ok: false, error: networkError() }
    }
}

/** Download a blob response (for report exports). */
export const portalDownload = async (
    url: string,
    data: unknown,
    filename: string,
): Promise<boolean> => {
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        })
        if (!res.ok) return false
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        a.click()
        URL.revokeObjectURL(a.href)
        return true
    } catch {
        return false
    }
}

/** Upload a FormData body (for document/avatar uploads). */
export const portalUpload = async <T>(url: string, formData: FormData): Promise<ApiResult<T>> => {
    try {
        const res = await fetch(url, {
            method: 'POST',
            body: formData,
        })
        if (!res.ok) return { ok: false, error: await parseError(res) }
        const body = (await res.json()) as ApiSuccess<T>
        return { ok: true, data: body.data }
    } catch {
        return { ok: false, error: networkError() }
    }
}
